// Calling a phone number into the room you are in (QUEUE row 67; Adam,
// 2026-09-28: "could i theoreticaly go to video call or audio call and just
// make the call to their actual phone number if they're not on smls?"). A
// sheet from the bottom: type a number or a saved name, or tap someone called
// before. The call goes out from the owner's own number and the person joins
// the room as a tile; nothing about Breeze's AI is involved.

import { useEffect, useRef, useState, type JSX } from "react";
import type { CadenceCall, CadencePerson } from "@breeze/core";
import { api, ApiError } from "../lib/api";
import type { RoomKind } from "../lib/wsClient";

interface PhoneDialerProps {
  readonly kind: RoomKind;
  readonly slug: string;
  readonly onClose: () => void;
  readonly onPlaced: (name: string) => void;
}

/** Who the typed text could mean: a name or any word of it starting with it,
 * then a name containing it, then a number containing the digits typed. With
 * nothing typed, the first few (people called most recently, then saved
 * contacts by name). */
function matchPeople(people: readonly CadencePerson[], query: string, limit: number): CadencePerson[] {
  const q = query.trim().toLowerCase();
  if (!q) return people.slice(0, limit);
  const qd = q.replace(/\D/g, "");
  const starts: CadencePerson[] = [];
  const within: CadencePerson[] = [];
  for (const p of people) {
    const name = p.name.toLowerCase();
    if (name.startsWith(q) || name.split(/\s+/).some((w) => w.startsWith(q))) starts.push(p);
    else if (name.includes(q) || (qd.length >= 3 && p.number.replace(/\D/g, "").includes(qd))) within.push(p);
  }
  const all = [...starts, ...within];
  // Once the one name this could be is typed out, there is nothing left to offer.
  if (all.length === 1 && all[0]?.name.toLowerCase() === q) return [];
  return all.slice(0, limit);
}

function pretty(e164: string): string {
  const d = e164.replace(/^\+1/, "");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : e164;
}

export function PhoneDialer({ kind, slug, onClose, onPlaced }: PhoneDialerProps): JSX.Element {
  const [value, setValue] = useState("");
  const [people, setPeople] = useState<readonly CadencePerson[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const controller = new AbortController();
    api<{ people: CadencePerson[] }>("/api/cadence/people", { signal: controller.signal })
      .then((r) => setPeople(r.people))
      .catch(() => undefined);
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      controller.abort();
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const suggestions = matchPeople(people, value, 6);

  const place = async (who: string, label: string): Promise<void> => {
    if (!who.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const call = await api<CadenceCall>("/api/cadence/calls", {
        method: "POST",
        body: { direct: true, who: who.trim(), room: { kind, slug } },
      });
      if (call.status === "failed") throw new ApiError(502, { message: call.error ?? "The call did not go through." });
      onPlaced(call.toName ?? label);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The call did not go through. Try again.");
      setBusy(false);
    }
  };

  return (
    <div
      role="presentation"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 40,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: "rgba(0,0,0,0.4)",
      }}
    >
      <form
        role="dialog"
        aria-label="Call a phone number"
        onSubmit={(e) => {
          e.preventDefault();
          void place(value, value);
        }}
        style={{
          width: "100%",
          maxWidth: 480,
          padding: "var(--space-4) var(--space-4) calc(env(safe-area-inset-bottom, 0px) + var(--space-4))",
          borderRadius: "18px 18px 0 0",
          background: "rgba(28,31,36,0.97)",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-3)",
          animation: "breeze-fade 0.2s var(--ease)",
        }}
      >
        <div style={{ fontSize: 17, fontWeight: 600 }}>Call a phone</div>
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-label="Number or name"
            placeholder="Number or name"
            inputMode="tel"
            autoComplete="tel"
            style={{
              flex: 1,
              minWidth: 0,
              height: 48,
              padding: "0 14px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.18)",
              background: "rgba(255,255,255,0.08)",
              color: "#fff",
              font: "inherit",
              fontSize: 17,
            }}
          />
          <button
            type="submit"
            disabled={busy || !value.trim()}
            style={{
              height: 48,
              padding: "0 20px",
              borderRadius: 12,
              border: "none",
              background: "var(--accent)",
              color: "var(--accent-contrast)",
              font: "inherit",
              fontSize: 16,
              fontWeight: 600,
              cursor: busy ? "default" : "pointer",
              opacity: busy || !value.trim() ? 0.6 : 1,
            }}
          >
            {busy ? "Calling" : "Call"}
          </button>
        </div>
        {error ? (
          <div role="alert" style={{ color: "#ff8a80", fontSize: 14 }}>
            {error}
          </div>
        ) : null}
        {suggestions.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {suggestions.map((p) => (
              <button
                key={p.number}
                type="button"
                disabled={busy}
                onClick={() => void place(p.number, p.name)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  minHeight: 46,
                  padding: "0 4px",
                  border: "none",
                  borderTop: "1px solid rgba(255,255,255,0.08)",
                  background: "transparent",
                  color: "#fff",
                  font: "inherit",
                  fontSize: 15,
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <span>{p.name}</span>
                <span style={{ color: "#aab2bb", fontVariantNumeric: "tabular-nums" }}>{pretty(p.number)}</span>
              </button>
            ))}
          </div>
        ) : null}
      </form>
    </div>
  );
}
